import React, { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useBooking } from "./BookingContext";
import { CALENDLY_URL } from "./content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const FIELDS = [
  { key: "name", label: "Full name", type: "text", placeholder: "Your name" },
  { key: "email", label: "Email", type: "email", placeholder: "you@email.com" },
  { key: "phone", label: "Phone", type: "tel", placeholder: "+91 00000 00000" },
];

export default function BookingModal() {
  const { open, closeBooking } = useBooking();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.phone) {
      setError("Please share your name, email, and phone.");
      return;
    }
    // Open the tab synchronously inside the click gesture to avoid popup blocking,
    // then navigate it once the lead is saved.
    const calendlyWindow = window.open("", "_blank", "noopener,noreferrer");
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      if (calendlyWindow) calendlyWindow.location.href = CALENDLY_URL;
    } catch (err) {
      if (calendlyWindow) calendlyWindow.close();
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    closeBooking();
    setTimeout(() => {
      setDone(false);
      setForm({ name: "", email: "", phone: "", message: "" });
      setError("");
    }, 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal"
          className="fixed inset-0 z-[80] flex items-stretch justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={reset} />
          <motion.div
            className="relative z-10 flex h-full w-full max-w-[560px] flex-col justify-center overflow-y-auto bg-warm px-8 py-16 md:px-16"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <button
              data-testid="booking-close"
              onClick={reset}
              className="absolute right-6 top-6 text-ink/60 hover:text-ink transition-colors"
              aria-label="Close"
            >
              <X strokeWidth={1.25} size={26} />
            </button>

            {!done ? (
              <>
                <span className="overline text-copper">Villa Design Strategy Call</span>
                <h3 className="mt-5 font-serif text-4xl md:text-5xl leading-[1.05] text-ink">
                  Begin your legacy.
                </h3>
                <p className="mt-5 text-ink/60 leading-relaxed">
                  Share a few details and we will open the calendar to reserve your private
                  30-minute session. Only a limited number are offered each month.
                </p>

                <form onSubmit={submit} className="mt-10 space-y-7" data-testid="booking-form">
                  {FIELDS.map((f) => (
                    <div key={f.key}>
                      <label className="overline text-ink/50">{f.label}</label>
                      <input
                        data-testid={`booking-input-${f.key}`}
                        type={f.type}
                        value={form[f.key]}
                        onChange={(e) => update(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        className="mt-2 w-full border-b border-ink/20 bg-transparent pb-3 text-lg text-ink placeholder:text-ink/30 focus:border-forest focus:outline-none transition-colors"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="overline text-ink/50">A little about your vision (optional)</label>
                    <textarea
                      data-testid="booking-input-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      rows={3}
                      placeholder="Plot, location, aspirations…"
                      className="mt-2 w-full resize-none border-b border-ink/20 bg-transparent pb-3 text-lg text-ink placeholder:text-ink/30 focus:border-forest focus:outline-none transition-colors"
                    />
                  </div>

                  {error && (
                    <p data-testid="booking-error" className="text-sm text-copper">
                      {error}
                    </p>
                  )}

                  <button
                    data-testid="booking-submit"
                    type="submit"
                    disabled={submitting}
                    className="group relative mt-2 w-full overflow-hidden bg-forest px-8 py-5 text-[0.78rem] uppercase tracking-[0.22em] text-warm disabled:opacity-60"
                  >
                    <span className="relative z-10">
                      {submitting ? "Reserving…" : "Continue To Calendar"}
                    </span>
                    <span className="absolute inset-0 origin-bottom scale-y-0 bg-copper transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-y-100" />
                  </button>
                </form>
              </>
            ) : (
              <div data-testid="booking-success" className="text-center">
                <span className="overline text-copper">Reserved</span>
                <h3 className="mt-5 font-serif text-4xl md:text-5xl leading-tight text-ink">
                  Opening your calendar…
                </h3>
                <p className="mt-5 text-ink/60 leading-relaxed">
                  Thank you, {form.name.split(" ")[0] || "friend"}. If the calendar didn&apos;t open,
                  <button
                    onClick={() => window.open(CALENDLY_URL, "_blank", "noopener,noreferrer")}
                    className="ml-1 text-forest link-underline"
                    data-testid="booking-manual-calendly"
                  >
                    click here
                  </button>
                  .
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
